// @flow

import { createSelector } from 'reselect';
import { reject, compact, entries, flow, get, groupBy, map, partition, sortBy } from 'lodash/fp';

import { getColumns, getDateString } from '../utils/challengeUtils';
import t from '../utils/translate';
import { DATE } from '../constants/groupByTypes';
import { IN_PROGRESS } from '../constants/challengeStatusTypes';
import createChallengeFilter from '../utils/createChallengeFilter';
import { FEATURED } from '../constants/challengeCategoryTypes';
import routeFilterSelector from './routeFilterSelector';

const closedAndNotEntered = c =>
  !c.hasEntered &&
  (c.statusId >= IN_PROGRESS || (c.entryCap > 0 && c.entryCount >= c.entryCap && !c.isUnlimited));

function getChallenges(
  rawChallenges: Array<Object>,
  groupByKey: string,
  routeFilter: (Array<Object>) => void,
  interactiveFilter: (Array<Object>) => void
) {
  const [rapidfireChallenges, otherChallenges] = partition(ch => ch.isRapidfire, rawChallenges);

  // [MC] consolidate rapidfire rows as we receive a row for each price bracket.
  // we only want to see 1 row for each competition type (nrl & afl)
  const reduceRapidfires = rapidfireChallenges.reduce((acc, challenge) => {
    if (acc[challenge.sportName]) return acc;

    return {
      ...acc,
      [challenge.sportName]: {
        ...challenge,
        userEntryCount: 0, // ensuring that aggregated rapidfire challenges will never look like they have entries
        userEntryIds: [],
        cost: undefined,
        name: challenge.rapidfireName,
        link: `/rapidfire/${challenge.realCompetitionId}`
      }
    };
  }, {});

  const [stickyChallenges, regularChallenges] = partition('sticky', otherChallenges);
  const sortAndFilterChallenges = flow(routeFilter, interactiveFilter, reject(closedAndNotEntered));
  const stickyAndRapidfireChallenges = [...Object.values(reduceRapidfires), ...stickyChallenges];
  const groupChallengesByDate = flow(
    groupBy(ch => new Date(ch.startTime).setHours(0, 0, 0, 0)),
    entries,
    map(([key, groupChallenges]) => ({
      key,
      title: `${t('Challenges')} - ${getDateString(parseInt(key, 10))}`,
      challenges: groupChallenges,
      columns: getColumns({ live: false, entered: false }),
      separate: false
    })),
    sortBy('key')
  );

  const finalStickyChallenges = sortAndFilterChallenges(stickyAndRapidfireChallenges);
  const stickyGroup =
    finalStickyChallenges.length > 0
      ? {
          key: 'sticky',
          title: t('Pinned Challenges'),
          challenges: finalStickyChallenges,
          columns: getColumns({ live: false, entered: false }),
          separate: true
        }
      : null;

  const filteredRegularChallenges = sortAndFilterChallenges(regularChallenges);

  if (filteredRegularChallenges.length === 0 && finalStickyChallenges.length === 0) {
    return [
      {
        key: 'none',
        title: t('Challenges'),
        challenges: [],
        columns: getColumns({ live: false, entered: false }),
        alwaysShow: true,
        emptyTitle: t('No Challenges'),
        emptyMessage: t('Upcoming challenges will appear here')
      }
    ];
  }

  if (groupByKey === DATE) {
    return compact([stickyGroup, ...groupChallengesByDate(filteredRegularChallenges)]);
  }

  return compact([
    stickyGroup,
    {
      key: 'regular',
      title: t('Challenges'),
      challenges: filteredRegularChallenges,
      columns: getColumns({ live: false, entered: false }),
      separate: false,
      alwaysShow: true,
      emptyTitle: t('No Challenges'),
      emptyMessage: t('Upcoming challenges will appear here')
    }
  ]);
}

const interactiveFilterSelector = createSelector(
  get('lobbyChallenges.activeCategory'),
  get('activeFilter'),
  get('lobbyChallenges.filters'),
  (category, activeFilter, filters) =>
    createChallengeFilter({
      category: activeFilter === FEATURED ? activeFilter : category,
      ...filters
    })
);

const lobbyChallengesSelector = createSelector(
  (state: GlobalState) => state.lobbyChallenges.challenges,
  (state: GlobalState) => state.lobbyChallenges.groupByKey,
  routeFilterSelector,
  interactiveFilterSelector,
  getChallenges
);

export default lobbyChallengesSelector;
